import { defineStore } from 'pinia';
import { createPopulateString, parseCollectionData } from '~/utils/strapiComponents/addStrapiData';
import { useStoreRoot } from '../abstract/root';
import { useStoreCountry } from '../client/country';
import { strapi } from '../transport/strapi';
import type { ICurrencyMenuData, ICurrencyMenuStrapiResponse } from '~/types/currency';

export const useStoreCurrencyMenu = defineStore('currencyMenu', {
  state: () => ({
    currencies: [] as ICurrencyMenuData[],
    requestPromise: null as Promise<ICurrencyMenuStrapiResponse> | null,
  }),

  getters: {
    activeCurrencies(): ICurrencyMenuData[] {
      const storeRoot = useStoreRoot();

      const currencies = this.currencies;

      if (storeRoot.isEstonianLicense) {
        return currencies.filter((c) => !c.crypto);
      }

      return currencies;
    },
    cryptoCurrencyCodes(): string[] {
      return this.currencies.filter((c) => c.crypto).map((c) => c.currencyCode);
    },
    fiatCurrencyCodes(): string[] {
      return this.currencies.filter((c) => !c.crypto).map((c) => c.currencyCode);
    },
    fiatCurrencies(): ICurrencyMenuData[] {
      return this.activeCurrencies.filter((c) => !c.crypto);
    },
    cryptoCurrencies(): ICurrencyMenuData[] {
      return this.activeCurrencies.filter((c) => c.crypto);
    },
    availableCurrencies(): ICurrencyMenuData[] {
      if (!useStoreCountry().country) {
        return this.currencies.filter(({ crypto }) => !!crypto);
      }

      const availableCurrencies = this.currencies.filter(
        ({ countriesExcluded }) =>
          !countriesExcluded?.length ||
          !countriesExcluded.includes(useStoreCountry().country?.toUpperCase())
      );

      return availableCurrencies;
    },
    defaultCurrencyForCountry(): ICurrencyMenuData | null {
      return (
        this.availableCurrencies.find((currency) =>
          currency.countriesDefault?.includes(useStoreCountry().country?.toUpperCase())
        ) || null
      );
    },
  },

  actions: {
    async loadCurrencyMenu() {
      if (!this.requestPromise) {
        const populateString = createPopulateString(['currencyIcon']);

        this.requestPromise = strapi()
          .get('currency-menus', populateString)
          .finally(() => (this.requestPromise = null));
      }

      const response = await this.requestPromise;

      if (response?.data) {
        const parsedData = parseCollectionData(response.data);
        const filteredState = this.currencies.filter(
          (item) => !parsedData.find(({ id }) => id && id === item.id)
        );

        this.currencies = [...filteredState, ...parsedData].sort((a, b) => a.position - b.position);

        return true;
      } else {
        return false;
      }
    },
    isCryptoCurency(currencyCode: string): boolean {
      return this.cryptoCurrencyCodes.includes(currencyCode);
    },
    isFiatCurrency(currencyCode: string): boolean {
      return this.fiatCurrencyCodes.includes(currencyCode);
    },
  },
});
